import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, Loader2, X } from "lucide-react";
import { useApi } from "../../context/ApiContext";
import { formatMoney } from "../../shared/utils/format";

export const inputClass =
  "w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 disabled:bg-gray-50 disabled:text-gray-400";

export const primaryButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

export const secondaryButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

export const linkButtonClass =
  "text-xs font-medium text-violet-700 hover:text-violet-900 hover:underline disabled:opacity-50 disabled:no-underline";

export const Field: React.FC<
  Readonly<{ label: string; hint?: string; children: React.ReactNode; className?: string }>
> = ({ label, hint, children, className }) => (
  <label className={`flex flex-col gap-1 text-xs font-medium text-gray-500 ${className ?? ""}`}>
    <span>{label}</span>
    {children}
    {hint && <span className="font-normal text-gray-400">{hint}</span>}
  </label>
);

export const ErrorText: React.FC<Readonly<{ message: string | null }>> = ({ message }) =>
  message ? (
    <p className="flex items-start gap-1.5 text-sm text-red-600">
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {message}
    </p>
  ) : null;

const Switch: React.FC<
  Readonly<{ checked: boolean; onChange: (next: boolean) => void; label: string; disabled?: boolean }>
> = ({ checked, onChange, label, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
      checked ? "bg-violet-600" : "bg-gray-300"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-4" : "translate-x-0.5"
      }`}
    />
  </button>
);

/** On/off switch. With `text`, the whole label is clickable, not just the switch. */
export const Toggle: React.FC<
  Readonly<{
    checked: boolean;
    onChange: (next: boolean) => void;
    label: string;
    text?: React.ReactNode;
    disabled?: boolean;
  }>
> = ({ checked, onChange, label, text, disabled }) => {
  const control = <Switch checked={checked} onChange={onChange} label={label} disabled={disabled} />;
  if (text === undefined) return control;
  return (
    <label
      className={`inline-flex items-center gap-2 text-sm text-gray-700 select-none ${
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
      }`}
    >
      {control}
      <span>{text}</span>
    </label>
  );
};

/** Centered form modal; Escape and backdrop close it unless a save is running. */
export const Modal: React.FC<
  Readonly<{
    title: string;
    onClose: () => void;
    busy?: boolean;
    width?: "md" | "lg" | "xl";
    footer?: React.ReactNode;
    children: React.ReactNode;
  }>
> = ({ title, onClose, busy, width = "lg", footer, children }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    globalThis.addEventListener("keydown", onKey);
    return () => globalThis.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const maxWidth = { md: "max-w-md", lg: "max-w-2xl", xl: "max-w-4xl" }[width];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/50 cursor-default"
        onClick={() => !busy && onClose()}
      />
      <div className={`relative flex max-h-[90vh] w-full ${maxWidth} flex-col rounded-xl bg-white shadow-2xl`}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
};

export const SaveButton: React.FC<
  Readonly<{ busy: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode }>
> = ({ busy, disabled, onClick, children }) => (
  <button type="button" className={primaryButtonClass} disabled={busy || disabled} onClick={onClick}>
    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
    {children}
  </button>
);

export const Chip: React.FC<Readonly<{ tone?: "violet" | "gray" | "amber" | "emerald" | "red"; children: React.ReactNode }>> = ({
  tone = "gray",
  children,
}) => {
  const tones = {
    violet: "bg-violet-50 text-violet-700",
    gray: "bg-gray-100 text-gray-700",
    amber: "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-700",
    red: "bg-red-50 text-red-700",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${tones[tone]}`}>
      {children}
    </span>
  );
};

export const money = (amount: string | number, currency: string) =>
  formatMoney(Number(amount), currency.toUpperCase());

export const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : "Something went wrong.";

/**
 * Load data and reload it on demand or when the API environment switches.
 * `load` must be stable (wrap it in useCallback); stale responses are dropped.
 */
export function useLoad<T>(load: () => Promise<T>) {
  const { refreshTrigger } = useApi();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef(0);

  const reload = useCallback(async () => {
    const id = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await load();
      if (id === requestRef.current) setData(result);
    } catch (e) {
      if (id === requestRef.current) setError(errorMessage(e));
    } finally {
      if (id === requestRef.current) setLoading(false);
    }
  }, [load]);

  useEffect(() => {
    reload();
  }, [reload, refreshTrigger]);

  return { data, loading, error, reload };
}

/** Run one write at a time and keep its error for the form. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async (action: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await action();
      return true;
    } catch (e) {
      setError(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, error, setError, run };
}

/** Datetime-local input value (local time) ↔ ISO string. */
export const toLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const fromLocalInput = (value: string): string | null => (value ? new Date(value).toISOString() : null);

/** Yes/no confirmation for actions that affect live customers. */
export const ConfirmModal: React.FC<
  Readonly<{
    title: string;
    message: React.ReactNode;
    confirmLabel: string;
    tone?: "danger" | "default";
    busy: boolean;
    error: string | null;
    onConfirm: () => void;
    onCancel: () => void;
  }>
> = ({ title, message, confirmLabel, tone = "default", busy, error, onConfirm, onCancel }) => (
  <Modal
    title={title}
    onClose={onCancel}
    busy={busy}
    width="md"
    footer={
      <>
        <button type="button" className={secondaryButtonClass} disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onConfirm}
          className={
            tone === "danger"
              ? "inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              : primaryButtonClass
          }
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {confirmLabel}
        </button>
      </>
    }
  >
    <div className="space-y-3 text-sm text-gray-600">
      {message}
      <ErrorText message={error} />
    </div>
  </Modal>
);
